import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, Area, AreaChart
} from 'recharts';
import api from '../../services/api';

const fmt = (n, decimals = 0) =>
  parseFloat(n).toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtRub = (n) => fmt(n, 2) + ' ₽';

const PERIODS = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: '90d', label: '90 дней' },
  { value: '1y', label: '1 год' },
  { value: 'all', label: 'Всё время' },
];

const GROUP_BY = [
  { value: 'day', label: 'По дням' },
  { value: 'week', label: 'По неделям' },
  { value: 'month', label: 'По месяцам' },
];

const KpiCard = ({ title, value, sub, color = 'indigo', icon }) => {
  const colors = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium uppercase tracking-wide opacity-70">{title}</p>
        {icon && <span className="text-lg">{icon}</span>}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-2">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span style={{ color: p.color }} className="text-xs">●</span>
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-medium">{fmtRub(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const MargineBar = ({ pct }) => {
  const p = Math.min(parseFloat(pct), 100);
  const color = p >= 30 ? 'bg-green-500' : p >= 15 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${p}%` }} />
      </div>
      <span className="text-xs text-gray-600">{parseFloat(pct).toFixed(1)}%</span>
    </div>
  );
};

const RevenueBar = ({ value, max }) => {
  const pct = max > 0 ? (parseFloat(value) / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-600">{fmtRub(value)}</span>
    </div>
  );
};

const AdminAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [groupBy, setGroupBy] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshingRate, setRefreshingRate] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, ratesRes] = await Promise.all([
        api.get('/admin/billing/analytics', { params: { Period: period, Group_By: groupBy } }),
        api.get('/admin/billing/exchange-rates', { params: { Limit: 30 } })
      ]);
      setAnalytics(analyticsRes.data);
      setExchangeRates(ratesRes.data);
    } catch (err) {
      toast.error('Ошибка загрузки аналитики: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [period, groupBy]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleRefreshRate = async () => {
    setRefreshingRate(true);
    try {
      const res = await api.post('/admin/billing/exchange-rates/refresh');
      toast.success(`Курс обновлён: 1 USD = ${parseFloat(res.data.Rate).toFixed(2)} ₽`);
      fetchAnalytics();
    } catch (err) {
      toast.error('Ошибка обновления курса: ' + (err.response?.data?.error || err.message));
    } finally {
      setRefreshingRate(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const { Summary, By_Client, By_Period } = analytics || {};

  const maxRevenue = By_Client?.length
    ? Math.max(...By_Client.map(c => parseFloat(c.Revenue_RUB)))
    : 1;

  const chartData = By_Period?.map(p => ({
    name: p.Period,
    'Выручка': parseFloat(p.Revenue_RUB),
    'Себестоимость': parseFloat(p.Cost_RUB),
    'Прибыль': parseFloat(p.Profit_RUB),
  })) || [];

  const rateHistory = exchangeRates?.History?.map(r => ({
    name: new Date(r.Fetched_At).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    Курс: parseFloat(r.Rate),
  })).reverse() || [];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 Аналитика выручки и прибыли</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {Summary?.Period_Label} · Курс USD/RUB: <strong>{parseFloat(Summary?.Current_Exchange_Rate || 0).toFixed(2)} ₽</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefreshRate}
            disabled={refreshingRate}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
          >
            <span className={refreshingRate ? 'animate-spin' : ''}>🔄</span>
            Обновить курс
          </button>
          <button
            onClick={fetchAnalytics}
            className="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Обновить
          </button>
        </div>
      </div>

      {/* Period / GroupBy selectors */}
      <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 p-3">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">Период:</span>
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                period === p.value
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="w-px h-5 bg-gray-200" />
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">Группировка:</span>
          {GROUP_BY.map(g => (
            <button
              key={g.value}
              onClick={() => setGroupBy(g.value)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                groupBy === g.value
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard
          title="Выручка"
          value={fmtRub(Summary?.Total_Revenue_RUB || 0)}
          sub="Списано с клиентов"
          color="indigo"
          icon="💰"
        />
        <KpiCard
          title="Себестоимость"
          value={fmtRub(Summary?.Total_Cost_RUB || 0)}
          sub="USD × курс"
          color="blue"
          icon="📦"
        />
        <KpiCard
          title="Прибыль"
          value={fmtRub(Summary?.Total_Profit_RUB || 0)}
          sub="Выручка − Себестоимость"
          color="green"
          icon="📈"
        />
        <KpiCard
          title="Маржа"
          value={parseFloat(Summary?.Gross_Margin_Pct || 0).toFixed(1) + '%'}
          sub="Gross Margin"
          color={parseFloat(Summary?.Gross_Margin_Pct || 0) >= 20 ? 'green' : 'amber'}
          icon="🎯"
        />
        <KpiCard
          title="Циклов"
          value={fmt(Summary?.Total_Cycles || 0)}
          sub="Всего обработано"
          color="slate"
          icon="🔄"
        />
        <KpiCard
          title="Курс USD"
          value={parseFloat(Summary?.Current_Exchange_Rate || 0).toFixed(2) + ' ₽'}
          sub="Актуальный курс"
          color="amber"
          icon="💱"
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { key: 'overview', label: 'Динамика' },
          { key: 'clients', label: 'По клиентам' },
          { key: 'rates', label: 'Курс USD/RUB' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.key
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Динамика */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {chartData.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
              Нет данных за выбранный период
            </div>
          ) : (
            <>
              {/* Revenue / Cost / Profit bar chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Выручка, себестоимость и прибыль</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmt(v)} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Выручка" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Себестоимость" fill="#93c5fd" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Прибыль" fill="#34d399" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Profit area chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Динамика прибыли</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
                    <defs>
                      <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmt(v)} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="Прибыль"
                      stroke="#10b981"
                      fill="url(#profitGrad)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab: По клиентам */}
      {activeTab === 'clients' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {!By_Client?.length ? (
            <div className="p-12 text-center text-gray-400">Нет данных</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      'Клиент', 'Наценка', 'Выручка', 'Себестоимость', 'Прибыль', 'Маржа', 'Циклы', 'Активность'
                    ].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {By_Client.map((client, i) => (
                    <tr key={client.User_ID} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 text-sm">
                          {client.Company_Name || '—'}
                        </div>
                        <div className="text-xs text-gray-400">{client.Email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                          +{parseFloat(client.Markup_Percent).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <RevenueBar value={client.Revenue_RUB} max={maxRevenue} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {fmtRub(client.Cost_RUB)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-semibold ${parseFloat(client.Profit_RUB) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtRub(client.Profit_RUB)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <MargineBar pct={client.Margin_Pct} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {fmt(client.Cycles_Count)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {client.Last_Activity
                          ? new Date(client.Last_Activity).toLocaleDateString('ru-RU')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-700" colSpan={2}>
                      Итого
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-indigo-600">
                      {fmtRub(Summary?.Total_Revenue_RUB || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-blue-600">
                      {fmtRub(Summary?.Total_Cost_RUB || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-600">
                      {fmtRub(Summary?.Total_Profit_RUB || 0)}
                    </td>
                    <td className="px-4 py-3">
                      <MargineBar pct={Summary?.Gross_Margin_Pct || '0'} />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-700">
                      {fmt(Summary?.Total_Cycles || 0)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Курс USD/RUB */}
      {activeTab === 'rates' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <KpiCard
              title="Текущий курс"
              value={parseFloat(Summary?.Current_Exchange_Rate || 0).toFixed(4) + ' ₽'}
              sub="1 USD → RUB"
              color="amber"
              icon="💱"
            />
            {rateHistory.length >= 2 && (
              <KpiCard
                title="Изменение"
                value={(() => {
                  const diff = rateHistory[rateHistory.length - 1]?.Курс - rateHistory[rateHistory.length - 2]?.Курс;
                  return (diff >= 0 ? '+' : '') + diff.toFixed(4) + ' ₽';
                })()}
                sub="За последний период"
                color={(() => {
                  const diff = rateHistory[rateHistory.length - 1]?.Курс - rateHistory[rateHistory.length - 2]?.Курс;
                  return diff >= 0 ? 'rose' : 'green';
                })()}
                icon="📉"
              />
            )}
            <KpiCard
              title="Записей в истории"
              value={exchangeRates?.History?.length || 0}
              sub="Обновлений за всё время"
              color="slate"
              icon="📋"
            />
          </div>

          {rateHistory.length > 1 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">История курса USD/RUB</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={rateHistory} margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
                  <defs>
                    <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 11 }}
                    tickFormatter={v => v.toFixed(2)}
                  />
                  <Tooltip formatter={(v) => [v.toFixed(4) + ' ₽', 'Курс']} />
                  <Line
                    type="monotone"
                    dataKey="Курс"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#f59e0b' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <p className="text-gray-400 text-sm">Недостаточно данных для построения графика</p>
              <p className="text-gray-400 text-xs mt-1">Курс обновляется автоматически раз в сутки</p>
            </div>
          )}

          {/* Rate history table */}
          {exchangeRates?.History?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Журнал обновлений</h3>
                <button
                  onClick={handleRefreshRate}
                  disabled={refreshingRate}
                  className="text-xs px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-50"
                >
                  {refreshingRate ? '⏳ Обновление...' : '🔄 Принудительно обновить'}
                </button>
              </div>
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата/время</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Курс (1 USD → RUB)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {exchangeRates.History.map((r, i) => (
                    <tr key={r.ID} className={i === 0 ? 'bg-amber-50' : 'bg-white'}>
                      <td className="px-4 py-2.5 text-sm text-gray-700">
                        {new Date(r.Fetched_At).toLocaleString('ru-RU', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                        {i === 0 && (
                          <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">
                            актуальный
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm font-mono font-semibold text-gray-900">
                        {parseFloat(r.Rate).toFixed(4)} ₽
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {r.Source}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAnalyticsDashboard;
